/* eslint-disable max-lines */
import {
    NestedPageLayout
} from 'shared/components/NewDesignComponents/PageLayout';
import Breadcrumb from 'shared/components/Breadcrumb';
import {
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {
    Button
} from 'shared/components/ui/button';
import {
    Copy as CopyIcon,
    RotateCcw,
    Wand2
} from 'lucide-react';
import {
    useNavigate
} from 'react-router-dom';
import EditorPanel, {
    EditorPanelHandle
} from './EditorPanel';
import TemplatesPanel from './TemplatesPanel';
import JsonHeader from './JsonHeader';
import {
    OpCodeBuilderContext
} from '../context/OpCodeBuilderContext';
import {
    Context
} from 'shared/context/GlobalState';
import {
    jsonOpcodeService
} from 'shared/api/services/BEX/jsonOpcode.service';
import {
    DEFAULT_OPCODE,
    STORAGE_KEY,
    ensureOpcodeShape,
    makeFlowTemplate,
    prepareOpcodeForDisplay,
    prepareOpcodeStringForDisplay,
    safeParseOpcode,
    sanitizeOpcodeForCreate
} from './templates';
import {
    OpcodeJsonShape,
    OpcodeStepTemplate
} from './types';

const JsonOpcodeCreate = () => {
    const [editorValue, setEditorValue] = useState<string>(prepareOpcodeForDisplay(DEFAULT_OPCODE));
    const [isValid, setIsValid] = useState<boolean>(true);
    const [validationMessage, setValidationMessage] = useState<string>('Valid JSON.');
    const [flowIndex, setFlowIndex] = useState<string>('0');
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const editorPanelRef = useRef<EditorPanelHandle>(null);
    const navigate = useNavigate();
    const {
        orgId,
        subOrgId
    } = useContext(OpCodeBuilderContext);
    const {
        addNewNotifcation
    } = useContext(Context);

    const parsedOpcode = useMemo(() => {
        const parsed = safeParseOpcode(editorValue);
        if (!parsed.ok) return null;
        return ensureOpcodeShape(parsed.value);
    }, [editorValue]);

    const counts = useMemo(() => {
        if (!parsedOpcode) {
            return {
                steps: 0,
                defs: 0,
                flows: 0
            };
        }
        return {
            steps: Array.isArray(parsedOpcode.steps) ? parsedOpcode.steps.length : 0,
            defs: Array.isArray(parsedOpcode.definitions) ? parsedOpcode.definitions.length : 0,
            flows: Array.isArray(parsedOpcode.flows) ? parsedOpcode.flows.length : 0
        };
    }, [parsedOpcode]);

    const setValidation = (ok: boolean, message: string) => {
        setIsValid(ok);
        setValidationMessage(message);
    };

    const validateLive = (text: string) => {
        const parsed = safeParseOpcode(text);
        if ('error' in parsed) {
            setValidation(false, parsed.error);
            // Highlight error line if line information is available (without moving cursor)
            if (parsed.line !== undefined) {
                editorPanelRef.current?.highlightErrorAt(parsed.line);
            }
            return;
        }
        ensureOpcodeShape(parsed.value);
        setValidation(true, 'Valid JSON.');
        // Clear error highlight when valid
        editorPanelRef.current?.clearErrorHighlight();
    };

    const loadInitial = () => {
        const saved = localStorage.getItem(STORAGE_KEY);
        const initial = saved
            ? prepareOpcodeStringForDisplay(saved)
            : prepareOpcodeForDisplay(DEFAULT_OPCODE);
        setEditorValue(initial);
        validateLive(initial);
    };

    useEffect(() => {
        loadInitial();
    }, []);

    useEffect(() => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        debounceRef.current = setTimeout(() => validateLive(editorValue), 200);

        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, [editorValue]);

    const persist = (text: string) => {
        localStorage.setItem(STORAGE_KEY, text);
    };

    const commitOpcode = (opcode: OpcodeJsonShape) => {
        // Strip internal fields before committing to ensure they never appear in editor
        const next = prepareOpcodeForDisplay(opcode);
        setEditorValue(next);
        persist(next);
        validateLive(next);
    };

    const getOpcodeOrError = () => {
        const parsed = safeParseOpcode(editorValue);
        if ('error' in parsed) {
            setValidation(false, 'Fix JSON first, then use buttons.');
            return null;
        }
        return ensureOpcodeShape(parsed.value);
    };

    const addToSteps = (step: OpcodeStepTemplate) => {
        const opcode = getOpcodeOrError();
        if (!opcode) return;
        opcode.steps.push(step);
        commitOpcode(opcode);
    };

    const addToDefinitions = (step: OpcodeStepTemplate) => {
        const opcode = getOpcodeOrError();
        if (!opcode) return;
        opcode.definitions.push(step);
        commitOpcode(opcode);
    };

    const addNewFlow = () => {
        const opcode = getOpcodeOrError();
        if (!opcode) return;
        opcode.flows.push(makeFlowTemplate());
        commitOpcode(opcode);
        setFlowIndex(String(opcode.flows.length - 1));
    };

    const addStepToSelectedFlow = (step: OpcodeStepTemplate) => {
        const opcode = getOpcodeOrError();
        if (!opcode) return;
        const idx = Number(flowIndex);
        if (!Number.isFinite(idx) || idx < 0 || idx >= opcode.flows.length) {
            setValidation(false, 'Select a valid flow first.');
            return;
        }
        opcode.flows[idx].steps = Array.isArray(opcode.flows[idx].steps) ? opcode.flows[idx].steps : [];
        opcode.flows[idx].steps.push(step);
        commitOpcode(opcode);
    };

    const formatInPlace = () => {
        const pretty = prepareOpcodeStringForDisplay(editorValue);
        if (pretty === editorValue) {
            // If prepareOpcodeStringForDisplay returned the original, it means parsing failed
            // Check if it's actually invalid JSON
            const parsed = safeParseOpcode(editorValue);
            if (!parsed.ok) {
                setValidation(false, 'Invalid JSON (cannot format).');
                return;
            }
        }
        setEditorValue(pretty);
        validateLive(pretty);
    };

    const reset = () => {
        localStorage.removeItem(STORAGE_KEY);
        const next = prepareOpcodeForDisplay(DEFAULT_OPCODE);
        setEditorValue(next);
        validateLive(next);
    };

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(editorValue);
            setValidation(true, 'Copied to clipboard.');
            setTimeout(() => validateLive(editorValue), 600);
        } catch (error) {
            console.error(error);
            setValidation(false, 'Clipboard blocked by browser.');
        }
    };

    const handleSaveOpcode = async () => {
        const parsed = safeParseOpcode(editorValue);
        if ('error' in parsed) {
            setValidation(false, parsed.error);
            if (addNewNotifcation) {
                addNewNotifcation(parsed.error, 'danger');
            }
            return;
        }

        const normalizedOpcode = ensureOpcodeShape(parsed.value);

        // Validate that name is present and non-empty
        if (!normalizedOpcode.name || typeof normalizedOpcode.name !== 'string' || normalizedOpcode.name.trim().length === 0) {
            const errorMessage = 'Name is required. Please provide a name for the opcode.';
            setValidation(false, errorMessage);
            if (addNewNotifcation) {
                addNewNotifcation(errorMessage, 'danger');
            }
            return;
        }

        if (!orgId || !subOrgId) {
            setValidation(false, 'Organization or Sub-organization is missing.');
            if (addNewNotifcation) {
                addNewNotifcation('Organization or Sub-organization is missing.', 'danger');
            }
            return;
        }

        // Sanitize opcode payload for create (remove id/_id/opcode_id, ensure name is present)
        const sanitizedOpcode = sanitizeOpcodeForCreate(normalizedOpcode);

        const opcodePayload = {
            organizationId: orgId,
            subOrganizationId: subOrgId,
            opcode: sanitizedOpcode
        };

        try {
            setIsSaving(true);
            await jsonOpcodeService.createJSONOpcode(opcodePayload);

            localStorage.removeItem(STORAGE_KEY);

            if (addNewNotifcation) {
                addNewNotifcation('JSON Opcode saved successfully.', 'success');
            }

            navigate(-1);
        } catch (error) {
            const err = error as { response?: { status?: number; data?: unknown } };
            const status = err.response?.status;
            const responseData = err.response?.data;

            if (status === 409) {
                addNewNotifcation?.('Opcode already exists. Please choose another unique name.', 'warning');
            } else if (typeof responseData === 'string' && responseData.trim().length > 0) {
                // Backend returned a specific user-facing error string
                addNewNotifcation?.(responseData, 'danger');
            } else {
                // Fallback to generic error
                addNewNotifcation?.('Opcode cannot be created from JSON. Please try again.', 'danger');
            }

            console.error('Error creating Opcode from JSON mode:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const flowOptionsLength = parsedOpcode?.flows?.length ?? 0;

    return (
        <NestedPageLayout>
            <div className="my-3">
                <Breadcrumb maxPaths={2} />
            </div>

            <div className="grid grid-rows-[auto,1fr] gap-4 bg-background text-foreground mt-2">
                <div className="px-6 pt-4">
                    <div className="flex items-start justify-between flex-wrap gap-4">
                        <div className="space-y-1">
                            <h1 className="text-2xl font-semibold">Create JSON Opcode</h1>
                            <p className="text-sm text-muted-foreground">
                                Edit opcodes in JSON directly. Buttons add valid templates into steps, definitions, or flows.
                            </p>
                        </div>
                        <div className="flex items-center justify-between w-full gap-3 flex-wrap">
                            <JsonHeader isValid={isValid} validationMessage={validationMessage} counts={counts} />
                            <div className="flex gap-2">
                                <Button variant="ghost" size="sm" onClick={formatInPlace} className="gap-2 text-primary">
                                    <Wand2 className="h-4 w-4" />
                                    Format
                                </Button>
                                <Button variant="ghost" size="sm" onClick={handleCopy} className="gap-2 text-emerald-500">
                                    <CopyIcon className="h-4 w-4" />
                                    Copy
                                </Button>
                                <Button variant="ghost" size="sm" onClick={reset} className="gap-2 text-destructive">
                                    <RotateCcw className="h-4 w-4" />
                                    Reset
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-[320px,1fr] gap-2 px-6 pb-6">

                    <TemplatesPanel
                        flowIndex={flowIndex}
                        flowOptionsLength={flowOptionsLength}
                        setFlowIndex={setFlowIndex}
                        addToSteps={addToSteps}
                        addToDefinitions={addToDefinitions}
                        addStepToSelectedFlow={addStepToSelectedFlow}
                        addNewFlow={addNewFlow}
                            />

                    <EditorPanel
                        ref={editorPanelRef}
                        editorValue={editorValue}
                        isValid={isValid}
                        validationMessage={validationMessage}
                        flowIndex={flowIndex}
                        onChange={(value) => {
                            setEditorValue(value);
                            persist(value);
                        }}
                    />
                </div>
                <div className="flex justify-end px-6 pb-6">
                    <Button
                        onClick={handleSaveOpcode}
                        disabled={isSaving || !parsedOpcode || !isValid}
                    >
                        {isSaving ? 'Creating...' : 'Create Opcode'}
                    </Button>
                </div>
            </div>
        </NestedPageLayout>
    );
};

export default JsonOpcodeCreate;
